import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  DollarSign,
  Megaphone,
  Shield,
  Rocket,
  Server,
  ListTodo,
  Lock,
} from 'lucide-react'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Action Plan
   Password-protected internal priorities (GTM, product, API, fundraising).
   ═══════════════════════════════════════════════════════════════════════════ */

const ACTION_PLAN_PASSWORD = 'dizee2026'

interface PlanSection {
  id: string
  number: number
  icon: typeof DollarSign
  title: string
  subtitle: string
  color: string
  bullets: string[]
}

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string; bannerBg: string }> = {
  emerald: {
    border: 'border-emerald-500/20',
    bg: 'bg-emerald-500/[0.04]',
    text: 'text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-300',
    bannerBg: 'bg-emerald-500/[0.06] border-emerald-500/15',
  },
  sky: {
    border: 'border-sky-500/20',
    bg: 'bg-sky-500/[0.04]',
    text: 'text-sky-400',
    pill: 'bg-sky-500/15 text-sky-300',
    bannerBg: 'bg-sky-500/[0.06] border-sky-500/15',
  },
  violet: {
    border: 'border-violet-500/20',
    bg: 'bg-violet-500/[0.04]',
    text: 'text-violet-400',
    pill: 'bg-violet-500/15 text-violet-300',
    bannerBg: 'bg-violet-500/[0.06] border-violet-500/15',
  },
  amber: {
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/[0.04]',
    text: 'text-amber-400',
    pill: 'bg-amber-500/15 text-amber-300',
    bannerBg: 'bg-amber-500/[0.06] border-amber-500/15',
  },
  cyan: {
    border: 'border-cyan-500/20',
    bg: 'bg-cyan-500/[0.04]',
    text: 'text-cyan-400',
    pill: 'bg-cyan-500/15 text-cyan-300',
    bannerBg: 'bg-cyan-500/[0.06] border-cyan-500/15',
  },
}

const sections: PlanSection[] = [
  {
    id: 'investment',
    number: 1,
    icon: DollarSign,
    title: 'Investment & fundraising',
    subtitle: 'Keep the raise moving; add net-new conversations.',
    color: 'emerald',
    bullets: [
      'Find more investors (explicit action item).',
      'Continue what we are doing: source new investors and set up more meetings.',
    ],
  },
  {
    id: 'outreach',
    number: 2,
    icon: ListTodo,
    title: 'Outreach pipeline',
    subtitle: 'Who we talk to before and around launch.',
    color: 'sky',
    bullets: [
      'Make a list of everyone we want to reach out to.',
      'Contact them.',
    ],
  },
  {
    id: 'gtm-comms',
    number: 3,
    icon: Megaphone,
    title: 'Go-to-market, social & PR',
    subtitle: 'Coordinate timing so hype lands when we want it to.',
    color: 'violet',
    bullets: [
      'Video: Ryan will handle.',
      'Decide social handle, other content, and timing.',
      'PR plan aligned to go live the same day we launch social media.',
      'Ryan: interviews, podcasts — product has not launched yet, but we are building hype.',
      'Put the full go-to-market strategy together.',
    ],
  },
  {
    id: 'trust-security',
    number: 4,
    icon: Shield,
    title: 'Trust, data & narrative',
    subtitle: 'Be ready when people ask how data is protected on DIZEE.',
    color: 'amber',
    bullets: [
      'Be able to answer clearly: is our data safe on DIZEE?',
      'Consider documenting a concrete mechanism or practice we use to protect data.',
      'Potentially explain Terms & Conditions and the security process where it helps close questions.',
    ],
  },
  {
    id: 'product-main',
    number: 5,
    icon: Rocket,
    title: 'Product (platform)',
    subtitle: 'Ship a tight first wedge, then expand.',
    color: 'cyan',
    bullets: [
      'Full harness setup.',
      'Create SIDEPIECE demo account.',
      'Quick launch on one pain point: tickets, release links, etc., then roll out the rest of the system.',
      'Reach out to managers to show them the system.',
    ],
  },
  {
    id: 'product-api',
    number: 6,
    icon: Server,
    title: 'Product (API)',
    subtitle: 'Position DIZEE as the system of record builders rely on.',
    color: 'sky',
    bullets: [
      'Complete the API to be the most compelling and reliable source of truth for music data: wrap Soundcharts into our API offering with enhancements we have already achieved, plus endpoints from DIZEE Intelligence.',
      'Reach out to everyone building anything in music — they need DIZEE to operate their business.',
    ],
  },
]

export default function ActionPlanPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set(sections.map((s) => s.id)))

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === ACTION_PLAN_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenSections(new Set(sections.map((s) => s.id)))
  const collapseAll = () => setOpenSections(new Set())

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8 bg-zinc-950">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">
                Action Plan
              </span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view the action plan.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
              />
              {error && <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
            >
              View plan
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.015] rotate-[-25deg]">
          <span className="text-[180px] font-bold tracking-widest text-white whitespace-nowrap select-none">
            CONFIDENTIAL
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full">
              Action Plan
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-10">
        <div>
          <h1 className="text-xl font-semibold text-white tracking-tight">Company action plan</h1>
          <p className="text-[13px] text-zinc-500 mt-2 max-w-2xl leading-relaxed">
            Consolidated priorities across fundraising, GTM, product launch, API, and trust. Same access as other
            internal plans.
          </p>
        </div>

        <nav className="flex flex-wrap gap-2">
          {sections.map((s) => {
            const c = colorMap[s.color] || colorMap.sky
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  if (!openSections.has(s.id)) toggleSection(s.id)
                  document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all hover:scale-[1.03] ${c.border} ${c.bg} ${c.text}`}
              >
                {s.number}. {s.title}
              </button>
            )
          })}
        </nav>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: 'Sections', value: sections.length },
            { label: 'Focus areas', value: 'GTM · Product · API · Raise' },
            { label: 'Owner note', value: 'Ryan — video & external comms' },
          ].map((card) => (
            <div key={card.label} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">{card.label}</div>
              <div className="text-lg font-semibold text-white">{card.value}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={expandAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Expand all
          </button>
          <span className="text-zinc-700">|</span>
          <button
            type="button"
            onClick={collapseAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Collapse all
          </button>
        </div>

        <div className="space-y-4">
          {sections.map((section) => {
            const isOpen = openSections.has(section.id)
            const c = colorMap[section.color] || colorMap.sky
            const Icon = section.icon

            return (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={`rounded-xl border ${c.border} ${c.bg} overflow-hidden transition-all scroll-mt-24`}
              >
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-4 px-6 py-5 text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.pill}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[11px] text-zinc-600 font-medium">#{section.number}</span>
                      <h2 className="text-[15px] font-semibold text-white">{section.title}</h2>
                    </div>
                    <p className="text-[12px] text-zinc-500 mt-1">{section.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 space-y-4">
                    <ul className="space-y-3">
                      {section.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${c.text.replace('text-', 'bg-')}`} />
                          <span className="text-[13px] text-zinc-300 leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </main>

      <footer className="border-t border-white/[0.06] mt-16">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-2">
          <span className="text-[11px] text-zinc-600">Confidential — internal use only</span>
          <span className="text-[11px] text-zinc-700">April 2026</span>
        </div>
      </footer>
    </div>
  )
}
